import { Product } from '../../core';
import { CustomFields, Expense, ListTemplate, Note, Organization, Shipment } from '../../types';
import Money from '../../utils/Money';
import { AddressComponent, AddressTemplateType } from '../components/address.template';
import { documentNotes } from '../components/documentNotes.template';
import { ExpensesComponent, ExpenseTemplateDataType } from '../components/expensesGrid.template';
import { OrganizationComponent } from '../components/organization.template';
import { ShipmentComponent, ShipmentTemplateType } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyTableMetadata, formatCurrency, formatDate, formatDecimal } from './formatters';

export type GrowerProductInputType = {
  organization: Organization;
  slug: string;
  status: string;
  harvestedAt: Date;
  submittedAt: Date;
  customFields: CustomFields;
  currencyCode: string;
  commissionRate?: number;
  commissionAmount?: number;
  productType: string;
  settlementBasis: string;
  harvestLocation: string;
  fieldTicketId: string;
  growerContact: { id: number; name: string; email: string };
  grower: { id: number; name: string };
  shipment: Shipment;
  lineItems: Array<{
    enteredByWeight: boolean;
    unitsOrdered: number;
    weightOrdered?: number;
    unitsReceived: number;
    weightReceived: number;
    lot: {
      slug: string;
      warehouseId: string;
      weightAvailable: number;
      quantity: number;
      initialQuantity: number;
      initialWeight: number;
      grade: string;
    };
    product: {
      commodity: { name: string };
      commodityStyle: { name: string };
      commoditySize: { name: string };
      commodityUnit: { name: string };
      commodityLabel: { name: string };
      grade: string;
      countryOfOrigin: string;
      weightPerInventoryUnit: number;
      organic: boolean;
      metrics: { netAvailable: number };
      variableWeight: boolean;
    };
  }>;
  expenses: Array<Expense>;
  notes: Note[];
};

export type GrowerProductTemplateDataType = Partial<{
  orderNumber: string;
  status: string;

  orderDate: string;
  harvestDate: string;
  terms: string;
  harvestLocation: string;
  fieldTicketId: string;

  grower: string;
  growerContact: string;
  growerEmail: string;
  growerAddress: AddressTemplateType;

  businessAddress: AddressTemplateType;

  lineItems: ListTemplate<
    Partial<{
      description: string;
      commodity: string;
      variety: string;
      size: string;
      unit: string;
      label: string;
      grade: string;
      organic: boolean;
      enteredByWeight: boolean;
      unitsOrdered: string;
      weightOrdered?: string;
      unitsReceived: string;
      weightReceived: string;
      lot: string;
    }>
  >;

  shipment: ShipmentTemplateType;

  // Charges/Expenses
  charges: ExpenseTemplateDataType;
  freightCharges: ExpenseTemplateDataType;
  chargesSubtotal: string;
  freightChargesSubtotal: string;

  // Computed totals
  productsSubtotal: string;
  totalWeight: string;
  totalUnits: string;
  orderTotal: string;

  customFields: CustomFields;
  notes: ListTemplate<{ text: string }>;
}>;

export const GrowerProductTemplate: DocumentTypePopulator<
  GrowerProductInputType,
  { productDescriptionTemplate?: string },
  GrowerProductTemplateDataType
> = {
  populate: (workOrder, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!workOrder) return {};

    const { organization } = workOrder;
    const primaryBusiness = organization?.places?.[0];

    const apExpenses = workOrder.expenses.filter(
      (expense) =>
        (expense.accountLevel === 'ACCOUNTS_PAYABLE' || expense.accountLevel === 'GROWER_PAYABLE') &&
        (!expense.businessEntityId || expense.businessEntityId === workOrder.grower?.id)
    );

    const apExpensesSubtotal = apExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);

    // Calculate totals
    const totalWeightReceived = workOrder.lineItems.reduce(
      (acc, item) =>
        acc + ((item.weightReceived || 0) ?? Product.Weight.of(item.product).forQuantity(item.unitsReceived)),
      0
    );
    const totalWeightOrdered = workOrder.lineItems.reduce(
      (acc, item) => acc + (item.weightOrdered ?? Product.Weight.of(item.product).forQuantity(item.unitsOrdered)),
      0
    );

    const totalUnitsReceived = workOrder.lineItems.reduce((acc, item) => acc + (item.unitsReceived || 0), 0);
    const totalUnitsOrdered = workOrder.lineItems.reduce((acc, item) => acc + (item.unitsOrdered || 0), 0);
    const commissionType = workOrder.commissionRate
      ? `${workOrder.commissionRate * 100}%`
      : `$${workOrder.commissionAmount}`;

    return {
      orderNumber: workOrder.slug,
      status: workOrder.status,
      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({ organization, customOrgName: globalOptions.customOrgName }),
      businessAddress: AddressComponent.templatize(primaryBusiness),
      phoneNumber: primaryBusiness?.phone,
      orderDate: formatDate(workOrder.submittedAt),
      harvestDate: formatDate(workOrder.harvestedAt),
      terms: workOrder?.productType === 'OWN_PRODUCT' ? 'Own Product' : `Commission: ${commissionType}`,
      grower: workOrder.grower?.name,
      growerContact: workOrder.growerContact?.name,
      growerEmail: workOrder.growerContact?.email,

      harvestLocation: workOrder.harvestLocation,
      fieldTicketId: workOrder.fieldTicketId,

      products: applyTableMetadata(
        workOrder.lineItems.map((lineItem) => {
          const weight = Product.Weight.of(lineItem.product);

          return {
            description: Product.productDescription(
              lineItem.product,
              productDescriptionTemplate ?? organization.productDescriptionTemplate
            ),
            commodity: lineItem.product.commodity.name,
            variety: lineItem.product.commodityStyle.name,
            size: lineItem.product.commoditySize.name,
            unit: lineItem.product.commodityUnit.name,
            label: lineItem.product.commodityLabel?.name,
            grade: lineItem.product.grade,
            organic: lineItem.product.organic,
            enteredByWeight: lineItem.enteredByWeight,
            unitsOrdered: formatDecimal(lineItem.unitsOrdered),
            weightOrdered: formatDecimal(
              lineItem.enteredByWeight ? lineItem.weightOrdered : weight.forQuantity(lineItem.unitsOrdered)
            ),
            unitsReceived: formatDecimal(lineItem.unitsReceived),
            weightReceived: formatDecimal(lineItem.weightReceived ?? weight.forQuantity(lineItem.unitsReceived)),
            lot: lineItem.lot?.slug,
            lotGrade: lineItem.lot?.grade,
          };
        })
      ),

      shipment: ShipmentComponent.templatize(workOrder.shipment),

      // Charges
      charges: ExpensesComponent.templatize(apExpenses, { currency: workOrder.currencyCode }),
      chargesSubtotal: formatCurrency(apExpensesSubtotal, workOrder.currencyCode),

      // Totals
      totalWeightOrdered: formatDecimal(totalWeightOrdered),
      totalWeightReceived: formatDecimal(totalWeightReceived),
      totalUnitsReceived: formatDecimal(totalUnitsReceived),
      totalUnitsOrdered: formatDecimal(totalUnitsOrdered),
      orderTotal: formatCurrency(apExpensesSubtotal, workOrder.currencyCode),

      notes: documentNotes(workOrder.notes, templateId, type),
      customFields: workOrder.customFields,
    };
  },

  definitionMap: {
    orderNumber: { label: 'Order Number', type: 'string' },
    status: { label: 'Status', type: 'string' },
    orderDate: { label: 'Order Date', type: 'string' },
    harvestDate: { label: 'Harvest Date', type: 'string' },
    terms: { label: 'Terms', type: 'string' },
    harvestLocation: { label: 'Harvest Location', type: 'string' },
    fieldTicketId: { label: 'Field Ticket ID', type: 'string' },
    grower: { label: 'Grower', type: 'string' },
    growerContact: { label: 'Grower Contact', type: 'string' },
    growerEmail: { label: 'Grower Email', type: 'string' },
    growerAddress: { label: 'Grower Address', type: 'address', children: AddressComponent.definitionMap },
    businessAddress: { label: 'Business Address', type: 'address', children: AddressComponent.definitionMap },
    lineItems: {
      label: 'Products',
      type: 'table',
      children: {
        description: { label: 'Description', type: 'string' },
        commodity: { label: 'Commodity', type: 'string' },
        variety: { label: 'Variety', type: 'string' },
        size: { label: 'Size', type: 'string' },
        unit: { label: 'Unit', type: 'string' },
        label: { label: 'Label', type: 'string' },
        enteredByWeight: { label: 'Bought by Weight', type: 'boolean' },
        grade: { label: 'Grade', type: 'string' },
        organic: { label: 'Organic', type: 'boolean' },
        unitsOrdered: { label: 'Units Ordered', type: 'string' },
        weightOrdered: { label: 'Weight Ordered', type: 'string' },
        unitsReceived: { label: 'Units Received', type: 'string' },
        weightReceived: { label: 'Weight Received', type: 'string' },
        lot: { label: 'Lot', type: 'string' },
      },
    },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'workOrder' },
    notes: { label: 'Notes', type: 'list', children: { text: { label: 'Text', type: 'string' } } },
    shipment: {
      label: 'Shipment',
      type: 'object',
      children: ShipmentComponent.definitionMap({ customFieldsRoot: 'workOrder' }),
    },
    charges: { label: 'Charges', type: 'table', children: ExpensesComponent.definitionMap },
    freightCharges: { label: 'Freight Charges', type: 'table', children: ExpensesComponent.definitionMap },
    chargesSubtotal: { label: 'Charges Subtotal', type: 'string' },
    freightChargesSubtotal: { label: 'Freight Charges Subtotal', type: 'string' },
    productsSubtotal: { label: 'Products Subtotal', type: 'string' },
    totalWeight: { label: 'Total Weight', type: 'string' },
    totalUnits: { label: 'Total Units', type: 'string' },
    orderTotal: { label: 'Order Total', type: 'string' },
  },
};
